import clsx from 'clsx';
import {
  type FocusEvent,
  forwardRef,
  type KeyboardEvent as ReactKeyboardEvent,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { useIntl } from 'react-intl';

import { XmarkIcon } from '@dynatrace/strato-icons';

import { DateSelector } from './DateSelector.js';
import * as styles from './DateTimePicker.sty.js';
import type { DateSelectorRef, DateTimePickerProps } from './types.js';
import { focusDateTimeInput } from './utils/focusDateTimeInput.js';
import { Button } from '../../buttons/button/Button.js';
import { useAriaLabelingProps } from '../../core/hooks/useAriaLabelingProps.js';
import { useBehavioralTrackingProps } from '../../core/hooks/useBehavioralTrackingProps.js';
import { useFocusRing } from '../../core/hooks/useFocusRing.js';
import { useId } from '../../core/hooks/useId.js';
import type { TimeErrorKey } from '../../core/time/shared.types.js';
import { useDateTimePickerStore } from '../../core/time/timeframeStore/useTimeframeStore.js';
import {
  formatTimeValidationMessage,
  getValidityStateKeyForTimeError,
} from '../../core/time/validation.js';
import { Flex } from '../../layouts/flex/Flex.js';
import { inputGroupClassName } from '../../layouts/input-group/InputGroup.sty.js';
import { InputGroup } from '../../layouts/input-group/InputGroup.js';
import { fieldTokens } from '../../styles/field.sty.js';
import { DateTimeInput } from '../date-time-range-picker/DateTimeInput.js';
import { dateTimeRange } from '../date-time-range-picker/DateTimeRangePicker.sty.js';
import { isExpressionTriggerKey } from '../date-time-range-picker/expression-trigger-helpers.js';
import { isContentModifyingKey } from '../date-time-range-picker/helpers.js';
import { onTimeframeCopyCapture } from '../date-time-range-picker/selection-copy-helpers.js';
import { useSelectionInteractionHandlers } from '../date-time-range-picker/useSelectionInteractionHandlers.js';
import type { ValidateFieldOptions } from '../form/form.types.js';
import { useInternalFormRegistration } from '../form/hooks/useInternalFormRegistration.js';
import { InternalBaseItem } from '../forms-core/components/form-field-messages/FormFieldMessageBaseItem.js';
import type { UseFormControlReturnType } from '../forms-core/hooks/useFormControl.js';
import { useMinMaxLogger } from '../forms-core/hooks/useMinMaxLogger.js';
import { FormFieldMessagesAction } from '../forms-core/state/formfield-messages-state-reducer.js';
import { useContainerFocus } from '../hooks/useContainerFocus.js';
import type { FormControlWithOverlayRef } from '../shared-types.js';
import {
  type ValidationState,
  type ValidationTrigger,
} from '../validation/types.js';
import { useCustomMessageEmitter } from '../validation/useCustomMessageEmitter.js';
import { useValidationStateEmitter } from '../validation/useValidationStateEmitter.js';
import { getValidationState } from '../validation/validation-builders.js';

const COMPONENT_NAME = 'DateTimePicker';

/**
 * Component to comfortably enter a date time value.
 * The `DateTimePicker` component automatically uses the user's settings for the
 * locale and timezone and formats the date and time accordingly.
 * @internal
 */
export const DateTimePickerRoot = forwardRef<
  FormControlWithOverlayRef<HTMLSpanElement>,
  Omit<
    DateTimePickerProps,
    | 'disabled'
    | 'required'
    | 'id'
    | 'aria-disabled'
    | 'readOnly'
    | 'controlState'
  > & {
    formControl: UseFormControlReturnType;
  }
>((props, forwardedRef) => {
  const {
    className: consumerClassName,
    style: consumerStyle,
    'data-testid': dataTestId,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
    placeholder,
    name,
    _form,
    _messageTarget,
    type = 'datetime',
    formControl,
    onValidityChange,
    onFocus,
    onBlur,
    ...remainingProps
  } = props;
  const {
    id,
    disabled,
    required,
    ariaDisabled,
    readOnly = false,
    inputProps,
    hintProps,
    hint,
    showError,
    messageState,
    messageDispatch: formFieldDispatch,
  } = formControl;

  const [behavioralTrackingProps] = useBehavioralTrackingProps(remainingProps);

  const min = useDateTimePickerStore((state) => state.min);
  const max = useDateTimePickerStore((state) => state.max);
  const precision = useDateTimePickerStore((state) => state.precision);
  const formatString = useDateTimePickerStore((state) => state.formatString);
  const value = useDateTimePickerStore((state) => state.dateTimePickerValue);
  const dateTimePickerError = useDateTimePickerStore(
    (state) => state.dateTimePickerError,
  );

  const setValue = useDateTimePickerStore(
    (state) => state.setDateTimePickerValue,
  );
  const dateInputRef = useDateTimePickerStore(
    (state) => state.dateTimeInputRef,
  );

  const rootRef = useRef<HTMLDivElement>(null);
  const dateSelectorRef = useRef<DateSelectorRef>(null);

  const [hasInputContent, setHasInputContent] = useState(false);

  // log to the sdk when min is larger than max.
  useMinMaxLogger(min, max, 'DateTimePicker');

  const ownerId = useId();

  const touchedContainerRef = useRef<HTMLDivElement>(null);

  const customMessagesRef = useRef(messageState.customMessages);
  customMessagesRef.current = messageState.customMessages;

  const disabledOrAriaDisabled = disabled || ariaDisabled || false;

  const ariaLabelingProps = useAriaLabelingProps(remainingProps);

  // No need to show the calendar widget if only a time value can be entered.
  const showDateSelector = type !== 'time';

  const { focusClassName } = useFocusRing({
    focusWithin: true,
    ignoreModality: true,
    color: showError ? 'critical' : 'neutral',
    disabled,
  });

  const intl = useIntl();
  const calendarTriggerLabel = intl.formatMessage({
    defaultMessage: 'Datepicker calendar trigger',
    id: 'Uuc6xAAPyctyHamM',
    description: 'Text shown for the datetimepicker calendar trigger.',
  });
  const clearButtonLabel = intl.formatMessage({
    defaultMessage: 'Clear date and time',
    id: '5i26Rr1LLz0OIGNJ',
    description: 'Aria label for the DateTimePicker clear button.',
  });

  const formValidityChangeRef = useRef<
    ((state: ValidationState) => void) | undefined
  >(undefined);
  const _emitValidationState = useValidationStateEmitter((state) =>
    formValidityChangeRef.current?.(state),
  );
  const emitValidationState = useCustomMessageEmitter(
    _emitValidationState,
    () => buildValidationState('manual', dateTimePickerError),
  );

  const buildValidationState = useCallback(
    (
      trigger: ValidationTrigger,
      key: TimeErrorKey | undefined,
    ): ValidationState => {
      if (key === undefined) {
        return { status: 'done', trigger, errors: [] };
      }

      const keyInstance = getValidityStateKeyForTimeError(key);

      return getValidationState(
        keyInstance,
        trigger,
        formatTimeValidationMessage(key, intl, {
          value,
          min,
          max,
          precision,
          expectedFormat: formatString,
        }),
        id || undefined,
      );
    },
    [intl, value, min, max, precision, formatString, id],
  );

  const { containerClassName, ...containerFocusProps } = useContainerFocus({
    onFocus,
    onBlur,
    disabled,
    overlayOwnerId: ownerId,
    emitValidationState,
    getBlurValidationState: () =>
      buildValidationState('blur', dateTimePickerError),
  });

  const validateField = useCallback(
    async ({
      reveal,
      trigger,
    }: ValidateFieldOptions): Promise<ValidationState> => {
      if (reveal) {
        formFieldDispatch?.({
          type: FormFieldMessagesAction.SETISTOUCHED,
          payload: { isTouched: true },
        });
      }
      return emitValidationState(
        buildValidationState(trigger, dateTimePickerError),
      );
    },
    [
      formFieldDispatch,
      emitValidationState,
      buildValidationState,
      dateTimePickerError,
    ],
  );

  const resetValidation = useCallback(() => {
    formFieldDispatch?.({
      type: FormFieldMessagesAction.SETISTOUCHED,
      payload: { isTouched: false, resetShowError: true },
    });

    if (dateTimePickerError) {
      formFieldDispatch?.({
        type: FormFieldMessagesAction.REMOVEMESSAGE,
        payload: { id: dateTimePickerError, messageType: 'formcontrol' },
      });
    }
    for (const msg of customMessagesRef.current) {
      formFieldDispatch?.({
        type: FormFieldMessagesAction.REMOVEMESSAGE,
        payload: { id: msg.id, messageType: 'custom' },
      });
    }
  }, [formFieldDispatch, dateTimePickerError]);

  const formRegistration = useInternalFormRegistration({
    id,
    name,
    form: _form,
    messageTarget: _messageTarget,
    onValidityChange,
    getValue: () => value,
    focus: () => dateInputRef.current?.inputRef?.focus(),
    validateField,
    resetValidation,
  });
  formValidityChangeRef.current = formRegistration.onValidityChange;

  // Provide root and input element reference and functions to interact with the calendar overlay
  useImperativeHandle(
    forwardedRef,
    (): FormControlWithOverlayRef<HTMLSpanElement> => ({
      element: rootRef.current,
      inputRef: dateInputRef.current?.inputRef,
      triggerRef: dateInputRef.current?.inputRef,
      open: () => dateSelectorRef.current?.open(),
      close: () => dateSelectorRef.current?.close(),
      focus() {
        dateInputRef.current?.inputRef?.focus();
      },
      validate() {
        dateInputRef.current?.validate();
        formFieldDispatch?.({
          type: FormFieldMessagesAction.SETISTOUCHED,
          payload: { isTouched: true },
        });
        emitValidationState(
          buildValidationState('manual', dateTimePickerError),
        );
      },
      // DEPRECATION: [APPDEV-18178] BREAKING-CHANGE: Remove validateAsync() and change validate() to return Promise<ValidationState>.
      validateAsync: async () => {
        dateInputRef.current?.validate();
        formFieldDispatch?.({
          type: FormFieldMessagesAction.SETISTOUCHED,
          payload: { isTouched: true },
        });
        const state = buildValidationState('manual', dateTimePickerError);
        return emitValidationState(state);
      },
    }),
    [
      formFieldDispatch,
      dateInputRef,
      emitValidationState,
      buildValidationState,
      dateTimePickerError,
    ],
  );

  // remove the textContent when the value is set to null.
  useEffect(() => {
    if (
      value === null &&
      dateInputRef.current?.expressionInputRef &&
      dateInputRef.current.expressionInputRef.textContent !== '' &&
      dateInputRef.current.element?.getAttribute('data-valuetype') !==
        'expression'
    ) {
      dateInputRef.current.expressionInputRef.textContent = '';
    }
  }, [value, dateInputRef]);

  useEffect(() => {
    emitValidationState(buildValidationState('change', dateTimePickerError));
  }, [value, emitValidationState, buildValidationState, dateTimePickerError]);

  const {
    onMouseDown,
    onKeyDown: handleSelectionKeyDown,
    onCutCapture,
    onContextMenu,
  } = useSelectionInteractionHandlers(
    touchedContainerRef,
    useCallback(() => {
      focusDateTimeInput(null, dateInputRef);
    }, [dateInputRef]),
    dateInputRef,
  );

  const onKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLDivElement>) => {
      if (disabled || readOnly) {
        handleSelectionKeyDown(event);
        return;
      }

      const isIso =
        dateInputRef.current?.element?.getAttribute('data-valuetype') ===
        'iso8601';

      if (isExpressionTriggerKey(event) && isIso) {
        dateInputRef.current?.setExpressionValue(event.key);
        requestAnimationFrame(() => {
          const expressionInputRef = dateInputRef.current?.expressionInputRef;
          if (expressionInputRef) {
            expressionInputRef.focus();
            const selection = window.getSelection();
            if (selection) {
              const range = document.createRange();
              range.selectNodeContents(expressionInputRef);
              range.collapse(false);
              selection.removeAllRanges();
              selection.addRange(range);
            }
          }
        });
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      if (isContentModifyingKey(event)) {
        dateInputRef.current?.clearPasteUndo();
      }

      handleSelectionKeyDown(event);
    },
    [disabled, readOnly, handleSelectionKeyDown, dateInputRef],
  );

  return (
    <Flex
      gap={4}
      flexDirection="column"
      data-testid={dataTestId}
      data-dtrum-mask={dataDtrumMask}
      data-dtrum-allow={dataDtrumAllow}
      ref={rootRef}
      className={clsx(containerClassName, consumerClassName)}
      style={consumerStyle}
      {...containerFocusProps}
      {...behavioralTrackingProps}
      data-dt-component={COMPONENT_NAME}
    >
      <InputGroup
        className={clsx(
          styles.dateTimePicker({
            type,
            disabled: disabledOrAriaDisabled,
            showError,
            readOnly,
          }),
        )}
        style={{ gridAutoColumns: '1fr auto' }}
      >
        <div
          ref={touchedContainerRef}
          className={clsx(
            fieldTokens({
              color: showError ? 'critical' : 'neutral',
              disabled: disabledOrAriaDisabled,
              variant: 'emphasized',
            }),
            dateTimeRange({
              showError,
              disabled: disabledOrAriaDisabled,
              readOnly,
            }),
            focusClassName,
            inputGroupClassName,
          )}
          tabIndex={disabled ? undefined : 0}
          onInput={() => {
            // true if the user typed an expression, or partially filled any ISO spin button
            setHasInputContent(
              !!dateInputRef.current?.expressionInputRef?.textContent ||
                Array.from(
                  dateInputRef.current?.spinButtonHandlers?.values() ?? [],
                ).some((h) => h?.element.current?.textContent),
            );
          }}
          onMouseDown={onMouseDown}
          onKeyDown={onKeyDown}
          onFocus={(e: FocusEvent) => {
            // Forward the focus only if it is coming from outside this component.
            // Focus the first spin button with textContent; otherwise, focus the input.
            if (!rootRef.current?.contains(e.relatedTarget)) {
              focusDateTimeInput(e, dateInputRef);
            }
          }}
          onCopyCapture={onTimeframeCopyCapture}
          onCutCapture={onCutCapture}
          onContextMenu={onContextMenu}
        >
          <DateTimeInput
            ref={dateInputRef}
            id={id}
            type={type}
            disabled={disabled}
            readOnly={readOnly}
            placeholder={placeholder}
            name={name}
            form={_form}
            value={value}
            onChange={(newValue) => {
              if (typeof newValue !== 'string') {
                setValue(newValue);
              }
            }}
            {...ariaLabelingProps}
            aria-disabled={ariaDisabled}
            required={required}
            validationValueMode="single"
            controlId={id}
            inputProps={inputProps}
            onPasteComplete={() => {
              requestAnimationFrame(() => {
                dateInputRef.current?.focusLast();
              });
            }}
          />
          {!readOnly &&
            !disabledOrAriaDisabled &&
            ((value !== null && value.value !== '') || hasInputContent) && (
              <Button
                onClick={() => {
                  // Focus before state updates so the button blurs with relatedTarget =
                  // expressionInputRef (inside boundary) rather than null (which would
                  // fire a spurious onBlur when the button unmounts after the clear).
                  dateInputRef.current?.expressionInputRef?.focus();

                  if (
                    dateInputRef.current?.expressionInputRef &&
                    dateInputRef.current.expressionInputRef.textContent !== ''
                  ) {
                    dateInputRef.current.expressionInputRef.textContent = '';
                  } else {
                    setValue(null);
                  }
                  setHasInputContent(false);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.stopPropagation();
                  }
                }}
                className={styles.clearIconButton}
                color={showError ? 'critical' : 'neutral'}
                size="condensed"
                aria-label={clearButtonLabel}
              >
                <XmarkIcon />
              </Button>
            )}
        </div>
        {showDateSelector && (
          <DateSelector
            ref={dateSelectorRef}
            aria-label={calendarTriggerLabel}
            aria-disabled={ariaDisabled}
            readOnly={readOnly}
            disabled={!!disabled}
            error={showError}
            className={clsx(styles.dateSelector, inputGroupClassName)}
            data-dtrum-mask={dataDtrumMask}
            data-dtrum-allow={dataDtrumAllow}
            overlayOwnerId={ownerId}
          />
        )}
      </InputGroup>
      {hint && (
        <InternalBaseItem
          variant={showError ? 'error' : 'hint'}
          disabled={disabled || ariaDisabled}
          {...hintProps}
        >
          {hint}
        </InternalBaseItem>
      )}
    </Flex>
  );
});

(
  DateTimePickerRoot as typeof DateTimePickerRoot & { displayName: string }
).displayName = COMPONENT_NAME;
